// Ported from dev-scrips-pm-react services/scheduling/use-previous-appointments.ts (PROD-1190 calendar port)
import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';

/**
 * POST /api/Appointment/CompletedAppointment
 *
 * Mirrors Flutter `previousAppointments()` →
 * `scheduling_data_source_impl.dart:606–624`. Returns the patient's
 * completed appointments with this provider — used to pick the
 * `referenceAppointmentId` for a follow-up create.
 */

export interface PreviousAppointment {
  appointmentId?: string;
  appointmentDate?: string;
  reasonCode?: { code?: string; display?: string; definition?: string };
  appointmentProfile?: {
    id?: string;
    profileName?: string;
    location?: { id?: string; name?: string };
  };
}

interface PreviousAppointmentsParams {
  organizationId: string;
  practiceId: string;
  providerId: string | null;
  patientId: string | null;
  /**
   * Caller gate — Flutter only loads previous appointments in the
   * FOLLOWUP flow (`previous_appointments_widget.dart`), so the section
   * passes `appointmentType === 'FOLLOWUP'`. Defaults to true to preserve
   * existing callers. Combined with the id/practiceId presence checks.
   */
  enabled?: boolean;
}

export function usePreviousAppointments(params: PreviousAppointmentsParams) {
  // `practiceId` MUST be a real GUID — the .NET binder rejects `""`
  // ("Error converting value '' to type 'System.Guid'"). The edit panel
  // hydrates provider+patient before a practice is resolved, so without
  // this guard the query fired with an empty practiceId and 400'd.
  const enabled =
    (params.enabled ?? true) &&
    !!params.providerId &&
    !!params.patientId &&
    !!params.practiceId;
  return useQuery({
    queryKey: [
      'previous-appointments',
      params.organizationId,
      params.practiceId,
      params.providerId,
      params.patientId,
    ],
    enabled,
    queryFn: ({ signal }) =>
      post<PreviousAppointment[]>(
        '/api/Appointment/CompletedAppointment',
        {
          organizationId: params.organizationId,
          practiceId: params.practiceId,
          providerId: params.providerId,
          patientId: params.patientId,
        },
        { signal },
      ),
  });
}
